import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

export interface DateValueParts {
  start: Dayjs;
  end?: Dayjs;
  hasTime: boolean;
}

const formatWithTime = 'YYYY-MM-DDTHH:mm';
const formatWithoutTime = 'YYYY-MM-DD';

export function parseDateValue(value?: string): DateValueParts | undefined {
  if (!value) return undefined;
  const parts = value.split('/');
  if (parts.length > 2) return undefined;

  const startStr = parts[0];
  const endStr = parts[1];

  const hasTime = startStr.includes('T');
  const start = dayjs(startStr, hasTime ? formatWithTime : formatWithoutTime);
  if (!start.isValid()) return undefined;

  let end: Dayjs | undefined;
  if (endStr) {
    end = dayjs(endStr, hasTime ? formatWithTime : formatWithoutTime);
    if (!end.isValid()) return undefined;
  }

  return { start, end, hasTime };
}

export function formatDateValue(parts: DateValueParts): string {
  const fmt = parts.hasTime ? formatWithTime : formatWithoutTime;
  const startStr = parts.start.format(fmt);
  if (parts.end) {
    return `${startStr}/${parts.end.format(fmt)}`;
  }
  return startStr;
}

export function startDayKey(value?: string): string | undefined {
  const parts = parseDateValue(value);
  if (!parts) return undefined;
  return parts.start.format('YYYY-MM-DD');
}

export function startSortKey(value?: string): string | undefined {
  const parts = parseDateValue(value);
  if (!parts) return undefined;
  return parts.start.format('YYYY-MM-DDTHH:mm');
}

/**
 * The shape BlockSuite database cells store a date in
 * (`@blocksuite/data-view` `normalizeDate` / `normalizeDateRange`).
 */
export interface NormalizedDateLike {
  start: number;
  end?: number;
  hasTime: boolean;
}

/**
 * Bridge between the database cell value (timestamps) and the string codec the
 * doc-property editors speak. Both directions live here so a range typed in the
 * info card survives the round trip.
 */
export function normalizedToDateValue(value: NormalizedDateLike): string {
  return formatDateValue({
    start: dayjs(value.start),
    end:
      value.end != null && value.end !== value.start
        ? dayjs(value.end)
        : undefined,
    hasTime: value.hasTime,
  });
}

export function dateValueToNormalized(
  value?: string
): NormalizedDateLike | undefined {
  const parts = parseDateValue(value);
  if (!parts) return undefined;
  return {
    start: parts.start.valueOf(),
    end: parts.end?.valueOf(),
    hasTime: parts.hasTime,
  };
}
